/* Cart drawer behaviour.

   Carried over from the prototype, adapted for the real Shopify cart: elements
   are looked up on demand (the drawer is a section refresh() can re-render),
   quantity/remove write through /cart/change.js with an optimistic DOM,
   refresh() re-renders via the Section Rendering API, and checkout reads
   data-cart-checkout-url (now /checkout) so the drawer goes straight to Shopify
   checkout — routed through window.CaratKinCheckoutGate first when the WhatsApp
   gate is enabled. Listeners are delegated on document, bound once.
*/

(() => {
  if (window.CaratKinCartDrawer) return;

  const root = () => window.Shopify?.routes?.root || "/";
  const drawerEl = () => document.querySelector("[data-cart-drawer]");
  const backdropEl = () => document.querySelector("[data-cart-backdrop]");
  const sectionEl = () => document.getElementById("shopify-section-cart-drawer");
  const within = (selector) => drawerEl()?.querySelector(selector) ?? null;
  const getItems = () => [...(drawerEl()?.querySelectorAll("[data-cart-item]") ?? [])];

  let lastFocusedElement = null;

  const money = (amount) => `₹${Math.max(0, amount).toLocaleString("en-IN")}`;
  const itemMoney = (amount) => `₹${Math.max(0, amount).toLocaleString("en-US")}`;

  // Show/hide a cart line. Setting the `hidden` property alone is not enough: the
  // line's own CSS sets `display`, which beats the `[hidden]` attribute, so a
  // removed row would keep showing until the drawer was re-rendered. Toggling
  // inline display as well makes the visual state always match.
  function setItemHidden(item, hidden) {
    item.hidden = hidden;
    item.style.display = hidden ? "none" : "";
  }

  function getItemQuantity(item) {
    return Number(item.querySelector("[data-cart-quantity-value]")?.textContent || 0);
  }

  function updateCart() {
    const drawer = drawerEl();
    if (!drawer) return;

    const countNode = within("[data-cart-count]");
    const subtotalNode = within("[data-cart-subtotal]");
    const emptyNode = within("[data-cart-empty]");
    const checkoutButton = within("[data-cart-checkout]");
    // The word beside the count is a theme setting, so it is read from the DOM
    // rather than hardcoded the way the prototype had it.
    const itemWord = countNode?.dataset.cartItemWord || "Item";

    const activeItems = getItems().filter((item) => !item.hidden);
    const totalQuantity = activeItems.reduce((sum, item) => sum + getItemQuantity(item), 0);
    const subtotal = activeItems.reduce(
      (sum, item) => sum + Number(item.dataset.unitPrice || 0) * getItemQuantity(item),
      0,
    );

    const itemWords = countNode?.dataset.cartItemWords || `${itemWord}s`;
    if (countNode) countNode.textContent = `(${totalQuantity} ${totalQuantity === 1 ? itemWord : itemWords})`;
    if (subtotalNode) subtotalNode.textContent = money(subtotal);
    if (emptyNode) emptyNode.hidden = activeItems.length > 0;
    if (checkoutButton) checkoutButton.disabled = totalQuantity === 0;

    activeItems.forEach((item) => {
      const quantity = getItemQuantity(item);
      const priceNode = item.querySelector("[data-cart-item-price]");
      if (priceNode) priceNode.textContent = itemMoney(Number(item.dataset.unitPrice || 0) * quantity);
    });

    // Keep the header's cart affordance truthful; nothing styles it, but it is
    // read by anything that wants the count without a round trip.
    document.querySelectorAll("a[data-cart-count]").forEach((node) => {
      node.dataset.cartCount = String(totalQuantity);
    });
  }

  /* Reconcile the drawer against Shopify's own view of the cart. Lines the cart no
     longer holds are hidden, quantities are corrected, then updateCart() recomputes
     every number from the corrected DOM so the money formatting stays in one place. */
  function applyCartState(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const byKey = new Map(cart.items.map((line) => [line.key, line]));

    getItems().forEach((item) => {
      const line = byKey.get(item.dataset.lineKey);
      if (!line) {
        setItemHidden(item, true);
        return;
      }
      setItemHidden(item, false);
      item.dataset.unitPrice = String(Math.round(line.final_price / 100));
      const valueNode = item.querySelector("[data-cart-quantity-value]");
      if (valueNode) valueNode.textContent = String(line.quantity);
    });

    updateCart();
  }

  /* Cart writes are serialised. Shopify rejects concurrent /cart/change.js calls
     with a 422, and rapid taps on + is exactly how that happens. */
  let pending = Promise.resolve();

  function changeLine(key, quantity) {
    if (!key) return;

    pending = pending
      .then(() => fetch(`${root()}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      }))
      .then((response) => {
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        return response.json();
      })
      .then(applyCartState)
      .catch((error) => {
        console.error(error);
        // Fall back to whatever the cart actually holds rather than leaving the
        // drawer showing an optimistic value the server refused.
        return fetch(`${root()}cart.js`, { headers: { Accept: "application/json" } })
          .then((response) => (response.ok ? response.json() : null))
          .then(applyCartState)
          .catch(() => {});
      });
  }

  /* Re-render the drawer from the server. Used after an add-to-cart, because the
     lines are server-rendered from cart.items and cannot be invented client-side
     without duplicating the whole card template in JavaScript. */
  async function refresh() {
    const host = sectionEl();
    if (!host) return;
    const wasOpen = drawerEl()?.classList.contains("is-open") === true;

    try {
      const response = await fetch(`${root()}?sections=cart-drawer`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`Cart drawer refresh failed: ${response.status}`);
      const payload = await response.json();
      const markup = payload["cart-drawer"];
      if (typeof markup !== "string") throw new Error("Cart drawer section missing from response");

      // The response is the section's own wrapper; only its contents are needed.
      const parsed = new DOMParser().parseFromString(markup, "text/html");
      const fresh = parsed.getElementById("shopify-section-cart-drawer") ?? parsed.body;
      host.innerHTML = fresh.innerHTML;

      if (wasOpen) {
        drawerEl()?.classList.add("is-open");
        drawerEl()?.setAttribute("aria-hidden", "false");
        const backdrop = backdropEl();
        if (backdrop) backdrop.hidden = false;
        document.body.classList.add("cart-open");
      }
      updateCart();
    } catch (error) {
      console.error(error);
    }
  }

  function openCart() {
    const drawer = drawerEl();
    const backdrop = backdropEl();
    if (!drawer || !backdrop) return;

    lastFocusedElement = document.activeElement;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    backdrop.hidden = false;
    document.body.classList.add("cart-open");
    within("[data-cart-close]")?.focus();
  }

  function closeCart() {
    const drawer = drawerEl();
    const backdrop = backdropEl();
    if (drawer) {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
    }
    if (backdrop) backdrop.hidden = true;
    document.body.classList.remove("cart-open");
    if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
      lastFocusedElement.focus();
    }
  }

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-cart-toggle]");
    if (toggle) {
      event.preventDefault();
      openCart();
      return;
    }

    if (event.target.closest("[data-cart-close]") || event.target === backdropEl()) {
      closeCart();
      return;
    }

    const removeButton = event.target.closest("[data-cart-remove]");
    if (removeButton) {
      const item = removeButton.closest("[data-cart-item]");
      if (item) {
        setItemHidden(item, true);
        updateCart();
        changeLine(item.dataset.lineKey, 0);
      }
      return;
    }

    const quantityButton = event.target.closest("[data-cart-quantity]");
    if (quantityButton) {
      const item = quantityButton.closest("[data-cart-item]");
      const valueNode = item?.querySelector("[data-cart-quantity-value]");
      if (!item || !valueNode) return;
      const current = Number(valueNode.textContent || 1);
      const next = quantityButton.dataset.cartQuantity === "increase"
        ? current + 1
        : Math.max(1, current - 1);
      if (next === current) return;
      valueNode.textContent = String(next);
      updateCart();
      changeLine(item.dataset.lineKey, next);
    }

    const checkoutButton = event.target.closest("[data-cart-checkout]");
    if (checkoutButton && !checkoutButton.disabled) {
      const destination = checkoutButton.dataset.cartCheckoutUrl || "/checkout";
      const proceed = () => { window.location.href = destination; };
      // If the WhatsApp gate is enabled and needs a number, let it take over and
      // proceed on our behalf once collected; otherwise go straight to checkout.
      if (window.CaratKinCheckoutGate && window.CaratKinCheckoutGate.intercept(proceed)) return;
      proceed();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && drawerEl()?.classList.contains("is-open")) closeCart();
  });

  // Always initialise closed. This also protects against stale classes when the
  // section is re-rendered by the theme editor.
  function reset() {
    const drawer = drawerEl();
    const backdrop = backdropEl();
    if (!drawer || !backdrop) return;
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    backdrop.hidden = true;
    document.body.classList.remove("cart-open");
    updateCart();
  }

  window.CaratKinCartDrawer = { open: openCart, close: closeCart, refresh, update: updateCart };

  reset();

  // Arriving from the /cart redirect (layout/theme.liquid): show the cart, then
  // drop the flag so a refresh or shared link does not reopen it.
  const params = new URLSearchParams(window.location.search);
  if (params.get("cart") === "open") {
    params.delete("cart");
    const query = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (query ? `?${query}` : "") + window.location.hash);
    openCart();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.id === "shopify-section-cart-drawer") reset();
  });
})();
